import { useState } from "react";
import ChecklistItemNoteSectionService, {
    ChecklistItemNoteSectionCreateParams,
} from "utilities/services/checklist-item-notes/checklist-item-note-section-service";
import { ChecklistItemNoteSectionRecord } from "models/view-models/checklist-item-notes/checklist-item-note-section-record";

interface UseChecklistItemNoteSection {
    createChecklistItemNoteSection: (
        checklistItemNoteId: number,
        sectionId: number
    ) => Promise<ChecklistItemNoteSectionRecord | null>;
    deleteChecklistItemNoteSection: (
        checklistItemNoteSectionId: number
    ) => Promise<void>;
    isSaving: boolean;
}

const useChecklistItemNoteSection = (): UseChecklistItemNoteSection => {
    const [isSaving, setIsSaving] = useState(false);
    const { create: createChecklistItemNoteSectionApi } =
        ChecklistItemNoteSectionService.useCreate();
    const { delete: deleteChecklistItemNoteSectionApi } =
        ChecklistItemNoteSectionService.useDelete();

    const createChecklistItemNoteSection = async (
        checklistItemNoteId: number,
        sectionId: number
    ): Promise<ChecklistItemNoteSectionRecord | null> => {
        setIsSaving(true);

        try {
            const requestData: ChecklistItemNoteSectionCreateParams = {
                checklistItemNoteId: checklistItemNoteId,
                sectionId: sectionId,
            };

            const response =
                await createChecklistItemNoteSectionApi(requestData);

            if (response?.result == null || response.resultObject == null) {
                throw new Error(
                    `No result returned when creating checklist item note section for note ${checklistItemNoteId} and section ${sectionId}`
                );
            }

            if (response.result.hasErrors()) {
                throw new Error(response.result.listErrorMessages().join(", "));
            }

            return response.resultObject;
        } catch (error) {
            console.error(error);

            return null;
        } finally {
            setIsSaving(false);
        }
    };

    const deleteChecklistItemNoteSection = async (
        checklistItemNoteSectionId: number
    ) => {
        if (!checklistItemNoteSectionId) {
            throw new Error("Checklist item note section ID is not valid");
        }

        setIsSaving(true);

        try {
            const deleteResponse = await deleteChecklistItemNoteSectionApi(
                checklistItemNoteSectionId,
                {
                    id: checklistItemNoteSectionId,
                }
            );

            if (
                deleteResponse?.result == null ||
                deleteResponse.resultObject == null
            ) {
                throw new Error(
                    `No result when deleting a checklist item note section with ID ${checklistItemNoteSectionId}`
                );
            }
            if (deleteResponse.result?.hasErrors()) {
                throw new Error(
                    deleteResponse.result.listErrorMessages().join(",")
                );
            }
        } catch (error) {
            throw error;
        } finally {
            setIsSaving(false);
        }
    };

    return {
        createChecklistItemNoteSection: createChecklistItemNoteSection,
        deleteChecklistItemNoteSection: deleteChecklistItemNoteSection,
        isSaving: isSaving,
    };
};

export default useChecklistItemNoteSection;
